import useSWR, { type SWRConfiguration } from 'swr';
import denApi from '@/lib/api/denApi';
import type { MinimalUser } from '@/types/user';
import type { Signature } from '@/types/signature';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import type {
  DomainInvalidReason,
  ProposalValidity,
} from '@mls-wallet/shared-types';
import { SIGNABLE_POLL_INTERVAL_MS } from '@/components/execution/execution-polling';

// FE-local types (avoid importing from @mls-wallet/database)
type OrgMemberProposalBase = {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  expirationTimestamp: number;
  validity: ProposalValidity;
  domainInvalidReason?: DomainInvalidReason;
  organizationId: string;
  proposedByUserId: string;
  createdAt: string;
  updatedAt: string;
};

export type OrgMemberProposal = OrgMemberProposalBase &
  (
    | {
        type: 'ADD';
        joiningUserId: string;
      }
    | {
        type: 'REMOVE';
        removingUserId: string;
      }
  );

export type PendingProposalWithSignatures = {
  proposal: OrgMemberProposal;
  isExecuting: boolean;
  approvals: Signature[];
  rejections: Signature[];
  requiredNetworkIds: number[];
  requiredApprovals: number;
  totalVoters: number;
  targetUser: MinimalUser;
  proposedBy: MinimalUser;
  approvalReviewers: MinimalUser[];
  rejectionReviewers: MinimalUser[];
};

const PENDING_MEMBER_PROPOSALS_PATH =
  'admin/organization/member-proposals/pending';

const listPendingMemberProposals = async () =>
  await denApi.get<PendingProposalWithSignatures[]>(
    PENDING_MEMBER_PROPOSALS_PATH
  );

/**
 * Returns pending organization member proposals ready for admin review.
 *
 * Expired proposals are renewed by the async worker, so this hook only reads
 * the currently live pending proposals from the sync server.
 */
export function usePendingMemberProposals(
  swrOptions?: SWRConfiguration<PendingProposalWithSignatures[]>
) {
  const { organization } = useMyOrganization();
  const isAdmin = Boolean(organization?.isAdmin);
  // Org context comes from x-current-org-id cookie (not URL params)
  const url = isAdmin ? PENDING_MEMBER_PROPOSALS_PATH : null;
  const { data, error, isLoading, mutate } = useSWR<
    PendingProposalWithSignatures[]
  >(url, listPendingMemberProposals, {
    revalidateOnFocus: false,
    refreshInterval: SIGNABLE_POLL_INTERVAL_MS,
    ...(swrOptions ?? {}),
  });

  return {
    proposals: data ?? [],
    error,
    isLoading,
    refresh: mutate,
  };
}
